/**
 * ActionCard — inline form for responding to info_request messages from the switch agent.
 * Renders inside inbox thread detail view when parsed_data.action_type === "info_request".
 */
import React, { useState } from 'react'
import { View, Text, TextInput, Pressable, StyleSheet, ActivityIndicator } from 'react-native'
import { CheckCircle, Send } from 'lucide-react-native'
import { colors, spacing, borderRadius, typography } from '../theme'
import { apiClient } from '../api/client'

interface ActionCardProps {
  messageId: string
  infoType: 'ean' | 'pbs' | 'phone' | 'iccid' | 'address' | 'other'
  question: string
  responded: any
  onRespond: () => void
}

const FIELD_CONFIG: Record<string, { label: string; placeholder: string; keyboardType?: string; multiline?: boolean }> = {
  ean: { label: 'EAN-nummer', placeholder: '571313... (18 cifre)' },
  pbs: { label: 'PBS-oplysninger', placeholder: 'Reg.nr Kontonr (fx 1234 5678901234)' },
  phone: { label: 'Telefonnummer', placeholder: '+45 12 34 56 78', keyboardType: 'phone-pad' },
  iccid: { label: 'SIM-kort (ICCID)', placeholder: '8945... (19-20 cifre)' },
  address: { label: 'Adresse', placeholder: 'Gadenavn 12, 1234 By' },
  other: { label: 'Svar', placeholder: 'Skriv dit svar...', multiline: true },
}

export function ActionCard({ messageId, infoType, question, responded, onRespond }: ActionCardProps) {
  const [value, setValue] = useState('')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState('')

  const config = FIELD_CONFIG[infoType] || FIELD_CONFIG.other

  if (responded) {
    return (
      <View style={styles.container}>
        <View style={styles.respondedRow}>
          <CheckCircle size={18} color={colors.success} />
          <Text style={styles.respondedText}>
            Sendt {responded.timestamp ? new Date(responded.timestamp).toLocaleDateString('da-DK') : ''}
          </Text>
        </View>
      </View>
    )
  }

  const handleSubmit = async () => {
    if (!value.trim()) {
      setError('Udfyld feltet')
      return
    }
    setError('')
    setSending(true)
    try {
      await apiClient.inbox.respond(messageId, { value: value.trim(), identifier_field: infoType })
      onRespond()
    } catch (e: any) {
      const detail = e?.response?.data?.detail
      setError(detail || 'Kunne ikke sende — prv igen')
    } finally {
      setSending(false)
    }
  }

  return (
    <View style={styles.container}>
      <Text style={styles.question}>{question}</Text>

      <Text style={styles.label}>{config.label}</Text>
      <TextInput
        style={[styles.input, config.multiline && styles.multilineInput]}
        placeholder={config.placeholder}
        placeholderTextColor={colors.text.muted}
        value={value}
        onChangeText={setValue}
        keyboardType={config.keyboardType as any || 'default'}
        multiline={config.multiline}
        autoCapitalize="none"
        autoCorrect={false}
      />

      {error ? <Text style={styles.error}>{error}</Text> : null}

      <Pressable
        style={[styles.button, sending && styles.buttonDisabled]}
        onPress={handleSubmit}
        disabled={sending}
      >
        {sending ? (
          <ActivityIndicator size="small" color="#fff" />
        ) : (
          <>
            <Send size={16} color="#fff" />
            <Text style={styles.buttonText}>Send svar</Text>
          </>
        )}
      </Pressable>
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: colors.primarySubtle,
    borderRadius: borderRadius.md,
    borderWidth: 1,
    borderColor: colors.primaryLight,
    padding: spacing.md,
    marginVertical: spacing.sm,
  },
  question: {
    ...typography.caption,
    color: colors.text.primary,
    marginBottom: spacing.sm,
  },
  label: {
    ...typography.small,
    color: colors.text.muted,
    marginBottom: 4,
  },
  input: {
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: borderRadius.sm,
    padding: spacing.sm,
    ...typography.caption,
    color: colors.text.primary,
  },
  multilineInput: {
    minHeight: 80,
    textAlignVertical: 'top',
  },
  error: {
    ...typography.small,
    color: colors.error,
    marginTop: 4,
  },
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    backgroundColor: colors.primary,
    borderRadius: borderRadius.sm,
    paddingVertical: 10,
    paddingHorizontal: spacing.md,
    marginTop: spacing.sm,
  },
  buttonDisabled: {
    opacity: 0.6,
  },
  buttonText: {
    ...typography.captionBold,
    color: '#fff',
  },
  respondedRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  respondedText: {
    ...typography.small,
    color: colors.success,
  },
})
